import React from "react";
import { type PersistentNotification, PersistentNotificationType } from "../../api";
import { observer } from "mobx-react";
import { action, computed, makeObservable } from "mobx";

import { store } from "../../store";
import { routePath } from "../../route-path";
import {
    NotificationCard,
    NotificationLink,
    NotificationRow,
    NotificationTitle,
} from "./notification-item";

export type ChecklistNotificationItemProps = {
    notification: PersistentNotification;
    markNotificationRead?: (id: string) => void;
};

/**
 * Renders both checklist notification types: one for a checklist that was
 * completed but failed, one for a checklist that was never completed because
 * the driver ran out of time. They carry the same fields and differ only in
 * what happened, so only the title tells them apart.
 */
@observer
export class ChecklistNotificationItem extends React.Component<ChecklistNotificationItemProps> {
    constructor(props: ChecklistNotificationItemProps) {
        super(props);
        makeObservable(this);
    }

    @computed private get title(): string {
        return this.props.notification.notificationType ===
            PersistentNotificationType.CHECKLIST_TIMEOUT
            ? store.i18n.t("component.notificationItemChecklist.timeoutTitle")
            : store.i18n.t("component.notificationItemChecklist.checklistTitle");
    }

    @action.bound private markRead(): void {
        if (this.props.markNotificationRead) {
            this.props.markNotificationRead(this.props.notification.id);
        }
    }

    render(): JSX.Element {
        const notification = this.props.notification;

        const vehicle = store.repositoryVehicles.byId(notification.vehicleId);
        if (!vehicle) {
            return <></>;
        }

        let fleetUserName = store.i18n.t("component.notificationItemChecklist.noOperator");
        if (notification.fleetUserId) {
            const fleetUser = store.repositoryUsers.byId(notification.fleetUserId);
            if (fleetUser) {
                fleetUserName = `${fleetUser.firstName} ${fleetUser.lastName}`;
            }
        }

        return (
            <NotificationCard markRead={!this.props.notification.read ? this.markRead : undefined}>
                <NotificationTitle to={routePath.preOpsChecklists()}>
                    {this.title}
                </NotificationTitle>
                <NotificationRow>
                    {store.i18n.formatDateTime(notification.triggeredAt)}
                </NotificationRow>
                <NotificationRow>
                    <span>{store.i18n.t("component.notificationItemChecklist.truck")}:</span>
                    <NotificationLink to={routePath.vehicles()}>
                        {vehicle.serialNumber}
                    </NotificationLink>
                </NotificationRow>
                <NotificationRow>
                    <span>{store.i18n.t("component.notificationItemChecklist.operator")}:</span>
                    <NotificationLink to={routePath.users()}>{fleetUserName}</NotificationLink>
                </NotificationRow>
            </NotificationCard>
        );
    }
}
